import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@lingui/react';
import { Redirect } from 'react-router-dom';
import get from 'lodash/get';
import transform from 'lodash/transform';
import omit from 'lodash/omit';

import { showArticoloByProfile, getPublicProductInfoMap, fetchPublicEtimClassDetails } from '../api';
import ArticleDetailsBody from './ArticleDetailsBody';
import AppLogo from './utils/AppLogo';
import PublicArticleLoginForm from './PublicArticleLoginForm';
import { normalizeArticleDetails, getDescrizioneMarcaFromArticolo, mergeArticleAssets, sortArticleAssets } from '../helpers';
import ScrollToTopButton from './utils/ScrollToTopButton';
import AppLoader from './utils/AppLoader';
import ErrorPage from './ErrorPage';
import { getLocaleFromLanguageCode } from '../intl-helpers';
import { setProductProperties } from '../actions/appActions';
import { selectors } from '../reducers/appReducer';
import { selectors as userSelectors } from '../reducers/userReducer';
import { setIsLoadingFromLocalStorage, setPublicRedirect } from '../actions/pageStateActions';
import { authSuccessfull } from '../actions/userActions';
import { setFilterValue } from '../actions/filtersActions';
import * as constants from '../constants';

function normalizeEtimClassDetails(etimClass) {
    return {
        ...etimClass,
        etim_features: transform(
            etimClass.etim_features,
            (collection, feature) => {
                collection[feature.id] = {
                    ...omit(feature, 'description'),
                    label: feature.description,
                };

                return collection;
            },
            {}
        ),
    };
}

const ArticlePublicProfile = ({ match, location, dispatch, language, scrollPos, onMount, i18n, user, enablePublicAccessRedirect }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [details, setDetails] = useState(null);
    const [article, setArticle] = useState(null);
    const [etimClassDetails, setEtimClassDetails] = useState(null);
    const [error, setError] = useState(false);
    const [shouldRedirect, setShouldRedirect] = useState(false);

    const getLocale = () => {
        return getLocaleFromLanguageCode(language);
    };

    // Handler per il login avvenuto con successo
    const handleLoginSuccess = (userData) => {
        // Dispatch auth
        dispatch(authSuccessfull(userData));

        // Setto il codice articolo nel filtro fullText
        const codiceArticolo = get(details, 'identificazione.codice_articolo.value');
        if (codiceArticolo) {
            dispatch(setFilterValue(constants.FULL_TEXT_FILTER, codiceArticolo));
        }

        // Setto il brand nel filtro
        const siglaMarca = get(details, 'identificazione.sigla_marca.value');
        if (siglaMarca) {
            dispatch(setFilterValue(constants.BRAND_FILTER, {
                label: siglaMarca.label ? siglaMarca.label[getLocale()] : siglaMarca.code,
                value: siglaMarca.id,
                code: siglaMarca.code,
            }));
        }

        // Setto i dati per aprire la modale nel DataPool
        dispatch(setPublicRedirect(details.id, siglaMarca?.code));

        setShouldRedirect(true);
    };

    const getMarcaArticolo = () => {
        return getDescrizioneMarcaFromArticolo(details, getLocale());
    };

    const getCodiceArticolo = () => {
        return get(details, 'identificazione.codice_articolo.value');
    };

    const getDescrizioneArticolo = () => {
        return get(
            details,
            `identificazione.descrizione_articolo.value.${getLocale()}`
        );
    };

    const profileSlug = useMemo(() => match.params.slug, [match.params.slug]);
    const brandCode = useMemo(() => match.params.brand, [match.params.brand]);

    // Memorizzo articleId estraendolo dal pathname perché il codice articolo può contenere "/"
    const articleId = useMemo(() =>
        location.pathname
            .replace(`/public/profile/${profileSlug}/product/`, '')
            .replace(brandCode + '/', '')
            .replace(/\/+$/, '')
    , [location.pathname, profileSlug, brandCode]);

    // Effetto per il componentDidMount
    useEffect(() => {
        const fetchData = async () => {
            // Nascondo il loader
            dispatch(setIsLoadingFromLocalStorage(false));

            let etimClassDetailsLocal = {};

            try {
                const articleRes = showArticoloByProfile(profileSlug, brandCode, articleId);

                const productInfoMapRes = getPublicProductInfoMap();

                const articleData = await articleRes;
                const productInfoMapData = await productInfoMapRes;

                const detailsData = normalizeArticleDetails(articleData.data);

                // Processa gli asset digitali dell'articolo (solo article_digital_asset dalla risposta)
                const mergedDetails = mergeArticleAssets(detailsData);
                const sortedArticleAssets = sortArticleAssets(mergedDetails.article_digital_asset, 'asset_classes.0.code');

                const finalDetails = {
                    ...mergedDetails,
                    article_digital_asset: sortedArticleAssets
                };

                if (finalDetails.etim && finalDetails.etim.id !== null) {
                    const etimClassRes = await fetchPublicEtimClassDetails(finalDetails.etim.id);
                    etimClassDetailsLocal = normalizeEtimClassDetails(etimClassRes.data);
                }

                let articleData_local = {};

                if (articleData.data && articleData.data.metadata.immagine_prodotto) {
                    articleData_local.immagine_prodotto = articleData.data.metadata.immagine_prodotto;
                }

                dispatch(setProductProperties(productInfoMapData.data));

                setDetails(finalDetails);
                setArticle(articleData_local);
                setEtimClassDetails(etimClassDetailsLocal);
                setIsLoading(false);
            } catch (e) {
                console.error(e);
                setError(e);
                setIsLoading(false);
            }
        };

        fetchData();
    }, [articleId, brandCode, profileSlug, dispatch, language]);

    // Effetto per il componentDidUpdate equivalente
    useEffect(() => {
        if (!isLoading && onMount) {
            onMount();
        }
    }, [isLoading, onMount]);

    // Se l'utente è già loggato e enablePublicAccessRedirect è attivo, redirect al DataPool
    if (enablePublicAccessRedirect && user !== null && details) {
        const codiceArticoloForFilter = get(details, 'identificazione.codice_articolo.value');
        if (codiceArticoloForFilter) {
            dispatch(setFilterValue(constants.FULL_TEXT_FILTER, codiceArticoloForFilter));
        }

        const siglaMarca = get(details, 'identificazione.sigla_marca.value');
        if (siglaMarca) {
            dispatch(setFilterValue(constants.BRAND_FILTER, {
                label: siglaMarca.label ? siglaMarca.label[getLocale()] : siglaMarca.code,
                value: siglaMarca.id,
                code: siglaMarca.code,
            }));
        }

        dispatch(setPublicRedirect(details.id, siglaMarca?.code));

        return <Redirect to="/" />;
    }

    if (shouldRedirect) {
        return <Redirect to="/" />;
    }

    if (isLoading) {
        return <AppLoader />;
    }

    if (error) {
        return (
            <ErrorPage
                message={i18n._('article:not:found')}
                showBackButton={false}
            />
        );
    }

    let btnStyle = {
        position: 'fixed',
        bottom: '10px',
    };

    if (window.innerWidth < 640) {
        btnStyle.left = '15px;';
    } else {
        btnStyle.right = '15px';
    }

    const showLoginForm = enablePublicAccessRedirect && user === null;

    return (
        <div className="bg-primary login-wrapper">
            <div className="container grid-xl">
                {showLoginForm && (
                    <PublicArticleLoginForm
                        i18n={i18n}
                        onLoginSuccess={handleLoginSuccess}
                    />
                )}
                <div className="form-spaced">

                    <div className="h5">
                        {getMarcaArticolo()}
                        {' - '}
                        {getCodiceArticolo()}
                        {' - '}
                        <span style={{ fontWeight: 400 }}>{getDescrizioneArticolo()}</span>
                    </div>
                    <ArticleDetailsBody
                        article={article}
                        details={details}
                        isPreview={false}
                        initialCategories={[]}
                        language={language}
                        etimClassDetails={etimClassDetails}
                    />
                    {scrollPos > 300 && (
                        <div style={btnStyle}>
                            <ScrollToTopButton inverse={true} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

function mapStateToProps(state) {
    return {
        language: selectors.getLanguage(state),
        user: userSelectors.getUser(state),
        enablePublicAccessRedirect: selectors.getEnablePublicAccessRedirect(state),
    };
}

const ConnectedArticlePublicProfile = connect(mapStateToProps)(ArticlePublicProfile);

export default withI18n()(ConnectedArticlePublicProfile);